// Deactivate confirm dialog (Step 5e Phase 7a + step-up Phase 4). Fetches the orphan-impact
// count first; for STEP-UP-gated targets (aircraft always; staff users) it embeds the password
// re-auth field and sends step_up. Student users (requiresStepUp=false) get the plain confirm.

import { useEffect, useState } from 'react';
import { C, Modal, Btn, ErrorText } from './adminUi';
import { StepUpFields } from './StepUp';
import type { Result } from '../../lib/admin';

export interface Impact { count: number; requiresStepUp: boolean }

export default function DeactivateDialog({ title, subject, reasons, loadImpact, onConfirm, onClose, onDone }: {
  title: string;
  subject: string;
  reasons: readonly string[];
  loadImpact: () => Promise<Impact>;
  onConfirm: (body: { reason: string; note?: string; step_up?: { password: string } }) => Promise<Result<unknown>>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [impact, setImpact] = useState<Impact | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { let live = true; loadImpact().then((i) => { if (live) setImpact(i); }); return () => { live = false; }; }, [loadImpact]);

  const requiresStepUp = impact?.requiresStepUp ?? false;
  const canConfirm = !!reason && (!requiresStepUp || password.length > 0) && impact !== null;

  async function confirm() {
    setBusy(true); setError(null);
    const res = await onConfirm({ reason, ...(note ? { note } : {}), ...(requiresStepUp ? { step_up: { password } } : {}) });
    setBusy(false);
    if (res.error) { setError(res.error); return; } // STEP_UP_FAILED / rate-limit messages surface here; user can retry
    onDone();
  }

  return (
    <Modal title={title} onClose={onClose} width={460}>
      <p style={{ fontSize: 13, color: C.textDim, margin: '0 0 12px' }}>Deactivate <b style={{ color: C.text }}>{subject}</b>? It drops off the board; its historical bookings are kept.</p>
      <div style={{ display: 'flex', gap: 10, background: impact?.count ? C.amberLight : C.surfaceAlt, border: `1px solid ${impact?.count ? C.amberBorder : C.border}`, color: impact?.count ? '#7a4a08' : C.textDim, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
        {impact === null ? 'Checking impact…' : impact.count === 0 ? 'No future bookings depend on this — nothing will be orphaned.' : <span><b>{impact.count} future booking(s)</b> will be moved to Orphaned bookings for review.</span>}
      </div>
      <label style={{ fontWeight: 600, fontSize: 12, color: C.textDim, display: 'block', marginBottom: 3 }}>Reason <span style={{ color: C.red }}>*</span></label>
      <select value={reason} onChange={(e) => setReason(e.target.value)} style={{ fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%', marginBottom: 10 }}>
        <option value="">— select —</option>
        {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <label style={{ fontWeight: 600, fontSize: 12, color: C.textDim, display: 'block', marginBottom: 3 }}>Note (optional)</label>
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} style={{ fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%' }} />
      {requiresStepUp && <StepUpFields password={password} setPassword={setPassword} />}
      {error && <ErrorText>{error}</ErrorText>}
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <Btn kind="danger" disabled={!canConfirm || busy} onClick={confirm}>{busy ? 'Deactivating…' : 'Deactivate'}</Btn>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}
